/**
 * MaintenantPage — Vue consolidée pour préparer une consultation
 *
 * Croise transits actifs (±30j), progressions serrées (orbe ≤ 0.5°),
 * RS en cours, et cycle de lunaison progressée.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import { useClient } from '../contexts/ClientContext.js';
import type { SuperpositionData, AspectInterTheme } from '../types/clients.js';

/** Aspect de transit */
interface TransitAspect {
  date: string;
  heure: string;
  planeteTransit: string;
  aspect: string;
  pointNatal: string;
  signe: string;
  position: string;
  vitesse: number | null;
  direction: string;
}

interface TransitsData {
  aspects: TransitAspect[];
  totalAspects: number;
  filteredCount: number;
  metadata: { dateDebut: string | null; dateFin: string | null };
}

/** Couleurs par nature d'aspect */
const ASPECT_COLORS: Record<string, { bg: string; text: string }> = {
  Conjonction: { bg: '#fefce8', text: '#854d0e' },
  Opposition: { bg: '#fef2f2', text: '#991b1b' },
  'Carré': { bg: '#fef2f2', text: '#991b1b' },
  Trigone: { bg: '#f0fdf4', text: '#166534' },
  Sextile: { bg: '#f0fdf4', text: '#166534' },
};

const PLANET_SYMBOLS: Record<string, string> = {
  Soleil: '☉', Lune: '☽', Mercure: '☿', 'Vénus': '♀', Mars: '♂',
  Jupiter: '♃', Saturne: '♄', Uranus: '♅', Neptune: '♆', Pluton: '♇',
  Chiron: '⚷', 'Noeud Nord': '☊', 'Noeud Sud': '☋', 'Lune Noire': '⚸',
  Ascendant: 'AS', 'Milieu du Ciel': 'MC',
};

const ASPECT_SYMBOLS: Record<string, string> = {
  Conjonction: '☌', Opposition: '☍', 'Carré': '□', Trigone: '△', Sextile: '✶',
};

const PLANETES_LENTES = new Set(['Jupiter', 'Saturne', 'Uranus', 'Neptune', 'Pluton', 'Chiron']);

/** Descriptions courtes des planètes en transit */
const TRANSIT_PLANET_DESC: Record<string, string> = {
  Pluton: 'Transformation profonde, mort/renaissance, pouvoir intérieur. Cycle très long — marque une génération entière.',
  Neptune: 'Dissolution des repères, idéalisation, ouverture spirituelle. Brouillard temporaire avant une nouvelle clarté.',
  Uranus: 'Rupture, libération, électrochoc. Ce qui était rigide vole en éclats pour laisser place à l\'authenticité.',
  Saturne: 'Structuration, responsabilité, confrontation au réel. Période d\'effort qui construit sur du solide.',
  Jupiter: 'Expansion, opportunités, confiance. Ouverture d\'un nouveau champ des possibles — attention à l\'excès.',
  Chiron: 'Activation d\'une blessure ancienne pour mieux la guérir. La vulnérabilité devient une force.',
  Mars: 'Énergie, initiative, affirmation. Impulsion à agir — gare à l\'impatience.',
  'Vénus': 'Relation, valeurs, plaisir. Attire l\'harmonie et les rencontres — ou questionne ce qu\'on aime.',
  Mercure: 'Communication, échanges, mental actif. Période propice aux démarches et aux apprentissages.',
  Soleil: 'Vitalité, conscience de soi. Éclaire la zone concernée pendant quelques jours.',
  Lune: 'Émotions, réceptivité, fluctuations rapides. Passage bref mais révélateur de besoins profonds.',
};

/** Descriptions des types d'aspect */
const ASPECT_DESC: Record<string, string> = {
  Conjonction: 'Fusion des énergies — début d\'un nouveau cycle. Intensification maximale, ni positive ni négative en soi.',
  Opposition: 'Polarité et prise de conscience. Tension entre deux principes qui demande un équilibre, une intégration.',
  'Carré': 'Friction créatrice — obstacle qui pousse à l\'action et au dépassement. Inconfort nécessaire à la croissance.',
  Trigone: 'Fluidité naturelle — les énergies coopèrent sans effort. Talents et facilités à exploiter consciemment.',
  Sextile: 'Opportunité à saisir — stimulation positive qui demande un petit effort pour se concrétiser.',
};

/** Descriptions des points nataux touchés */
const POINT_NATAL_DESC: Record<string, string> = {
  Soleil: 'identité profonde, vitalité, chemin de vie',
  Lune: 'émotions, besoins, monde intérieur, lien maternel',
  Mercure: 'mental, communication, façon de penser',
  'Vénus': 'valeurs, amour, esthétique, rapport à l\'argent',
  Mars: 'énergie d\'action, désir, combativité',
  Jupiter: 'foi, expansion, quête de sens',
  Saturne: 'structure, responsabilité, peurs, maturité',
  Uranus: 'besoin de liberté, originalité, intuition',
  Neptune: 'idéal, imaginaire, spiritualité, confusion',
  Pluton: 'pouvoir personnel, transformation, ombres',
  Chiron: 'blessure fondamentale, guérison, enseignement',
  Ascendant: 'apparence, approche de la vie, premier souffle',
  'Milieu du Ciel': 'vocation, image sociale, réalisation',
  'Noeud Nord': 'direction d\'évolution, axe karmique',
  'Noeud Sud': 'acquis passés, zone de confort à dépasser',
  'Lune Noire': 'zone d\'ombre, désir profond, refoulé',
};

/** Génère une explication combinée pour un aspect de transit */
function buildTransitExplanation(planeteTransit: string, aspect: string, pointNatal: string): string {
  const parts: string[] = [];
  const pDesc = TRANSIT_PLANET_DESC[planeteTransit];
  if (pDesc) parts.push(`**${planeteTransit} en transit** — ${pDesc}`);
  const aDesc = ASPECT_DESC[aspect];
  if (aDesc) parts.push(`**${aspect}** — ${aDesc}`);
  const nDesc = POINT_NATAL_DESC[pointNatal];
  if (nDesc) parts.push(`Touche **${pointNatal} natal** (${nDesc}).`);
  return parts.join('\n\n');
}

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 8, padding: '1.25rem',
  marginBottom: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

function formatDateFR(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function MaintenantPage() {
  const { id } = useParams<{ id: string }>();
  const { client } = useClient();

  const [transitsData, setTransitsData] = useState<TransitsData | null>(null);
  const [progData, setProgData] = useState<SuperpositionData | null>(null);
  const [rsData, setRsData] = useState<SuperpositionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState<{ text: string; key: string } | null>(null);

  // Identifier les charts pertinents
  const transitChart = useMemo(() =>
    client?.charts.filter((c) => c.type === 'TRANSITS')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0],
    [client]
  );

  const progChart = useMemo(() =>
    client?.charts.filter((c) => c.type === 'PROGRESSIONS')
      .sort((a, b) => (b.anneeRs ?? 0) - (a.anneeRs ?? 0))[0],
    [client]
  );

  const currentYear = new Date().getFullYear();
  const rsChart = useMemo(() =>
    client?.charts.filter((c) => c.type === 'REVOLUTION_SOLAIRE')
      .sort((a, b) => Math.abs((a.anneeRs ?? 0) - currentYear) - Math.abs((b.anneeRs ?? 0) - currentYear))[0],
    [client, currentYear]
  );

  useEffect(() => {
    if (!id || !client) return;

    const promises: Promise<void>[] = [];

    // Transits actifs (±30 jours)
    if (transitChart) {
      const now = new Date();
      const debut = new Date(now); debut.setDate(debut.getDate() - 30);
      const fin = new Date(now); fin.setDate(fin.getDate() + 30);
      const params = new URLSearchParams({
        dateDebut: formatDateFR(debut),
        dateFin: formatDateFR(fin),
        exclureLune: 'true',
      });

      promises.push(
        apiFetch<TransitsData>(`/clients/${id}/charts/${transitChart.id}/transits?${params}`)
          .then((res) => { if (res.success && res.data) setTransitsData(res.data); })
      );
    }

    // Progressions (superposition la plus récente)
    if (progChart) {
      promises.push(
        apiFetch<SuperpositionData>(`/clients/${id}/charts/${progChart.id}/superposition`)
          .then((res) => { if (res.success && res.data) setProgData(res.data); })
      );
    }

    // RS en cours
    if (rsChart) {
      promises.push(
        apiFetch<SuperpositionData>(`/clients/${id}/charts/${rsChart.id}/superposition`)
          .then((res) => { if (res.success && res.data) setRsData(res.data); })
      );
    }

    Promise.all(promises).finally(() => setLoading(false));
  }, [id, client, transitChart, progChart, rsChart]);

  // Transits groupés par planète
  const transitsByPlanet = useMemo(() => {
    if (!transitsData) return new Map<string, TransitAspect[]>();
    const map = new Map<string, TransitAspect[]>();
    for (const a of transitsData.aspects) {
      const list = map.get(a.planeteTransit) ?? [];
      list.push(a);
      map.set(a.planeteTransit, list);
    }
    // Trier : planètes lentes d'abord
    return new Map([...map.entries()].sort((a, b) => {
      const aLent = PLANETES_LENTES.has(a[0]) ? 0 : 1;
      const bLent = PLANETES_LENTES.has(b[0]) ? 0 : 1;
      return aLent - bLent || a[0].localeCompare(b[0]);
    }));
  }, [transitsData]);

  // Progressions serrées (orbe ≤ 0.5°)
  const progSerres = useMemo(() => {
    if (!progData) return [];
    return progData.superposition.aspectsInterThemes
      .filter((a) => a.orbe <= 0.5)
      .sort((a, b) => a.orbe - b.orbe);
  }, [progData]);

  // RS : 3 aspects les plus serrés
  const rsTopAspects = useMemo(() => {
    if (!rsData) return [];
    return [...rsData.superposition.aspectsInterThemes]
      .sort((a, b) => a.orbe - b.orbe)
      .slice(0, 3);
  }, [rsData]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted }}>
        Consolidation des données...
      </div>
    );
  }

  const noData = !transitChart && !progChart && !rsChart;

  return (
    <div>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.4rem', fontWeight: 700, color: colors.text }}>
        <i className="bi bi-clock" style={{ color: '#7c3aed', marginRight: 8 }} />
        Maintenant
      </h1>

      {noData && (
        <div style={card}>
          <p style={{ color: colors.textMuted, textAlign: 'center', margin: 0 }}>
            Aucune donnée disponible. Importez des transits, progressions ou RS pour alimenter cette vue.
          </p>
        </div>
      )}

      {/* Section 1 — Transits actifs */}
      {transitChart && (
        <div style={card}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-arrow-repeat" style={{ color: '#0891b2' }} />
            Transits actifs (±30 jours)
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: colors.textMuted }}>
              Lune exclue
            </span>
          </h2>

          {transitsByPlanet.size === 0 ? (
            <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
              Aucun transit dans cette période.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {[...transitsByPlanet.entries()].map(([planete, aspects]) => (
                <div key={planete}>
                  <div style={{
                    fontWeight: PLANETES_LENTES.has(planete) ? 700 : 500,
                    fontSize: '0.85rem', marginBottom: '0.25rem',
                    color: PLANETES_LENTES.has(planete) ? colors.text : colors.textMid,
                  }}>
                    {PLANET_SYMBOLS[planete] ?? ''} {planete}
                    {PLANETES_LENTES.has(planete) && (
                      <span style={{ fontSize: '0.7rem', background: '#ede9fe', color: '#5b21b6', padding: '1px 6px', borderRadius: 4, marginLeft: 6 }}>
                        lente
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {aspects.map((a, i) => {
                      const ac = ASPECT_COLORS[a.aspect] ?? { bg: '#f5f5f5', text: '#666' };
                      const key = `${planete}-${a.aspect}-${a.pointNatal}-${i}`;
                      const isOpen = tooltip?.key === key;
                      return (
                        <span key={i} style={{ position: 'relative' }}>
                          <button
                            onClick={() => setTooltip(isOpen ? null : { key, text: buildTransitExplanation(a.planeteTransit, a.aspect, a.pointNatal) })}
                            style={{
                              background: ac.bg, color: ac.text,
                              padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem',
                              border: isOpen ? `2px solid ${ac.text}` : '1px solid transparent',
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            {ASPECT_SYMBOLS[a.aspect] ?? ''} {a.aspect} {a.pointNatal}
                            <span style={{ marginLeft: 4, opacity: 0.7 }}>{a.date}</span>
                          </button>
                          {isOpen && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, zIndex: 50,
                              marginTop: 6, width: 320, padding: '12px 14px',
                              background: '#1e1b4b', color: '#e0e7ff', borderRadius: 8,
                              fontSize: '0.8rem', lineHeight: 1.6, boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                              whiteSpace: 'pre-line',
                            }}>
                              {tooltip.text.split('**').map((part, j) =>
                                j % 2 === 1
                                  ? <strong key={j} style={{ color: '#c7d2fe' }}>{part}</strong>
                                  : <span key={j}>{part}</span>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); setTooltip(null); }}
                                style={{
                                  position: 'absolute', top: 4, right: 8,
                                  background: 'none', border: 'none', color: '#a5b4fc',
                                  cursor: 'pointer', fontSize: '1rem', fontFamily: 'inherit',
                                }}
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Section 2 — Aspects progressés actifs */}
      {progChart && (
        <div style={card}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-clock-history" style={{ color: '#7c3aed' }} />
            Aspects progressés actifs
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: colors.textMuted }}>
              orbe ≤ 0.5°
            </span>
          </h2>

          {progSerres.length === 0 ? (
            <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
              Aucun aspect progressé très serré actuellement.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {progSerres.map((a, i) => (
                <AspectRow key={i} aspect={a} showTresSerré />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Section 3 — Rappel RS en cours */}
      {rsChart && rsData && (
        <div style={card}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-sun" style={{ color: '#16a34a' }} />
            RS {rsChart.anneeRs ?? currentYear}
          </h2>

          {/* AS et MC de la RS */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ background: colors.bgPage, borderRadius: 6, padding: '0.5rem 0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: colors.textMuted }}>AS annuel</div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                {rsData.superposition.axesAnnuels.ascendant.signe} {rsData.superposition.axesAnnuels.ascendant.degre}
                {rsData.superposition.axesAnnuels.ascendant.maisonNatale != null && (
                  <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}> (M{rsData.superposition.axesAnnuels.ascendant.maisonNatale})</span>
                )}
              </div>
            </div>
            <div style={{ background: colors.bgPage, borderRadius: 6, padding: '0.5rem 0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: colors.textMuted }}>MC annuel</div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                {rsData.superposition.axesAnnuels.mc.signe} {rsData.superposition.axesAnnuels.mc.degre}
                {rsData.superposition.axesAnnuels.mc.maisonNatale != null && (
                  <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}> (M{rsData.superposition.axesAnnuels.mc.maisonNatale})</span>
                )}
              </div>
            </div>
          </div>

          {/* Top 3 aspects RS×TN */}
          {rsTopAspects.length > 0 && (
            <>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textMid, marginBottom: '0.5rem' }}>
                Aspects RS×TN les plus serrés
              </div>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {rsTopAspects.map((a, i) => (
                  <AspectRow key={i} aspect={a} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Section 4 — Cycle de lunaison progressée */}
      {progData?.superposition.phaseLunaison && (
        <div style={card}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-moon" style={{ color: '#6366f1' }} />
            Lunaison progressée
          </h2>
          <p style={{ margin: 0, fontSize: '0.9rem', color: colors.text }}>
            {progData.superposition.phaseLunaison}
          </p>
        </div>
      )}
    </div>
  );
}

/** Ligne d'aspect inter-thèmes (RS ou Prog) — cliquable pour explication */
function AspectRow({ aspect, showTresSerré }: { aspect: AspectInterTheme; showTresSerré?: boolean }) {
  const [open, setOpen] = useState(false);
  const nature = aspect.nature;
  const bg = nature === 'tension' ? '#fef2f2' : nature === 'harmonie' ? '#f0fdf4' : '#fefce8';
  const color = nature === 'tension' ? '#991b1b' : nature === 'harmonie' ? '#166534' : '#854d0e';
  const explanation = buildTransitExplanation(aspect.planeteRS, aspect.type, aspect.planeteTN);

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
          padding: '0.4rem 0.75rem', borderRadius: 6, background: bg, fontSize: '0.8rem',
          border: open ? `2px solid ${color}` : '1px solid transparent',
          cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {PLANET_SYMBOLS[aspect.planeteRS] ?? ''} {aspect.planeteRS}
        </span>
        <span style={{ color }}>{aspect.symbole} {aspect.type}</span>
        <span style={{ fontWeight: 600 }}>
          {PLANET_SYMBOLS[aspect.planeteTN] ?? ''} {aspect.planeteTN}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: colors.textMuted }}>
          {aspect.orbe.toFixed(2)}°
        </span>
        {showTresSerré && aspect.orbe <= 0.2 && (
          <span style={{ background: '#dc2626', color: '#fff', padding: '1px 6px', borderRadius: 4, fontSize: '0.65rem', fontWeight: 600 }}>
            Très serré
          </span>
        )}
        {aspect.angulaire && (
          <span style={{ background: '#eff6ff', color: '#1e40af', padding: '1px 6px', borderRadius: 4, fontSize: '0.65rem', fontWeight: 600 }}>
            Angulaire
          </span>
        )}
      </button>
      {open && (
        <div style={{
          marginTop: 4, padding: '12px 14px',
          background: '#1e1b4b', color: '#e0e7ff', borderRadius: 8,
          fontSize: '0.8rem', lineHeight: 1.6, whiteSpace: 'pre-line',
        }}>
          {explanation.split('**').map((part, j) =>
            j % 2 === 1
              ? <strong key={j} style={{ color: '#c7d2fe' }}>{part}</strong>
              : <span key={j}>{part}</span>
          )}
        </div>
      )}
    </div>
  );
}
